import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeWords } from "@ts/utils";
import RevstackFiltersMenu from "./RevstackFiltersMenu";
import TopLevelFilters, { FilterTypes } from "./TopLevelFilters";
import {
    IMainFilterState,
    IAdditionalFiltersHandlers,
    RevstackFiltersSearchActions,
    TRevstackFiltersSearchAction,
    TRevstackFiltersSearchState,
    IMainFiltersValue,
    IMainFiltersData,
    IAdditionalFiltersValue,
    ITagTypeOption,
    IRevstackFilterOption,
} from "@/ts/common-types";
import { RevstackFiltersInitialSearchState } from "@/ts/constants";
import { updateMainFilters } from "@/features/revstackWidgetsSlice";

interface ISelectedFilters {
    categories: number[];
    subcategories: number[];
    brands: number[];
    tagTypeListLoading?: boolean;
}

interface IRevstackFiltersSubheaderProps {
    isLoading: boolean;
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
    tagTypeListLoading: boolean;
    additionalFiltersState: IMainFilterState;
}   

interface ISubheaderRootState {
    additionalFilters: { value: IAdditionalFiltersValue };
    mainFilters: { value: IMainFiltersValue };
    mainFiltersData: { value: IMainFiltersData };
}

const RevstackFiltersSubheader: React.FC<IRevstackFiltersSubheaderProps> = ({
    isLoading,
    expanded,
    setExpanded,
    tagTypeListLoading,
    additionalFiltersState,
}) => {
    const additionalFilters = useSelector((state: ISubheaderRootState) => state.additionalFilters?.value || { tagTypes: [], state: { isOpen: false, appliedFilters: {}, draftFilters: {}, activeIndex: 0, searchText: "" } });
    const tagTypes: ITagTypeOption[] = additionalFilters.tagTypes || [];
    const mainFilters = useSelector((state: ISubheaderRootState) => state.mainFilters.value);
    const mainFiltersData = useSelector(
        (state: ISubheaderRootState) => state.mainFiltersData.value
    );
    const dispatch = useDispatch();
    // Update selected state when mainFiltersData changes
    useEffect(() => {
        if (!mainFiltersData) return;

        const areSeletedFiltersAvailable = mainFilters.categories.length > 0;

        if (areSeletedFiltersAvailable) {
            dispatch(
                updateMainFilters({
                    categories: [...mainFilters.categories],
                    subcategories: [...mainFilters.subcategories],
                    brands: [...mainFilters.brands],
                })
            );
        } else if (mainFiltersData.selected) {
            dispatch(
                updateMainFilters({
                    categories: [...mainFiltersData.selected.categories],
                    subcategories: [...mainFiltersData.selected.subcategories],
                    brands: [...mainFiltersData.selected.brands],
                })
            );
        }
    }, [mainFiltersData]);

    const [draftSelected, setDraftSelected] =
        useState<ISelectedFilters>(mainFilters);
    const [appliedSelected, setAppliedSelected] =
        useState<ISelectedFilters>(mainFilters);
    const searchReducer = (
        state: TRevstackFiltersSearchState,
        action: TRevstackFiltersSearchAction
    ): TRevstackFiltersSearchState => {
        switch (action.type) {
            case "SET_SEARCH":
                return { ...state, [action.tab]: action.value };
            case "RESET_SEARCH":
                return { ...RevstackFiltersInitialSearchState };
            default:
                return state;
        }
    };

    const [searchState, dispatchSearch] = useReducer(
        searchReducer,
        RevstackFiltersInitialSearchState
    );

    const setSearchByTab = (
        tab: keyof TRevstackFiltersSearchState,
        value: string
    ) => {
        dispatchSearch({
            type: RevstackFiltersSearchActions.SET_SEARCH,
            tab,
            value,
        });
    };

    const resetSearchInAllTabs = () => {
        dispatchSearch({ type: RevstackFiltersSearchActions.RESET_SEARCH });
    };

    const [activeTab, setActiveTab] = useState<FilterTypes>(
        FilterTypes.CATEGORIES
    );
    const [brandType, setBrandType] = useState("mybrand");

    const selectedCategoryLabel = useMemo(() => {
        if (
            !appliedSelected?.categories?.length ||
            !mainFiltersData?.categories?.length
        ) {
            return "-";
        }

        const selectedCategoryIds = appliedSelected.categories;
        const categoryLabels = selectedCategoryIds.map((categoryId: number) => {
            const category = mainFiltersData.categories.find(
                (cat: IRevstackFilterOption) => cat.value === categoryId
            );
            return category?.label || `Category ${categoryId}`;
        });

        const remainingSelectedCatsCount = categoryLabels.length - 1;

        return categoryLabels.length > 0
            ? `${categoryLabels[0]}${
                  remainingSelectedCatsCount > 0
                      ? ` +${remainingSelectedCatsCount}`
                      : ""
              }`
            : "-";
    }, [appliedSelected, mainFiltersData]);

    const filtersCloseHandler = () => {
        setDraftSelected(appliedSelected);
        setExpanded(false);
        resetSearchInAllTabs();
        setActiveTab(FilterTypes.CATEGORIES);
    };  

    useEffect(() => {
        // Close top level filters when additional filters menu is open and top level filters are expanded
        if (additionalFiltersState?.isOpen && expanded) {
            filtersCloseHandler();
        }
    }, [additionalFiltersState?.isOpen, expanded]);

    return (
        <Row
            className="w-100 border-bottom border-1 bg-white py-3 px-5"
            align="middle"
            style={{ minHeight: 92 }}
        >
            <Col flex="3" className="d-flex flex-column justify-content-center">
                <h4 className="fw-bolder mb-2">
                    {capitalizeWords(selectedCategoryLabel)}
                </h4>
                {/* <Space size={8}>
                    <Tag className="title-products-count" bordered={false}>
                        Total Products: -
                    </Tag>
                </Space> */}
            </Col>
            <Col
                flex="3"
                className="d-flex align-items-center justify-content-end gap-2 w-100 px-0"
                style={{
                    ...(isLoading
                        ? {
                              opacity: 0.5,
                              pointerEvents: "none",
                          }
                        : {}),
                }}
            >
                <TopLevelFilters
                    {...{
                        mainFilters,
                        expanded,
                        setExpanded,
                        activeTab,
                        setActiveTab,
                        search: searchState[activeTab],
                        setSearch: (val: string) =>
                            setSearchByTab(activeTab, val),
                        resetSearchInAllTabs,
                        brandType,
                        setBrandType,
                        draftSelected,
                        setDraftSelected,
                        appliedSelected,
                        setAppliedSelected,
                        filtersCloseHandler,
                    }}
                    hideFilterPopover={isLoading}
                />
                <RevstackFiltersMenu 
                    filterData={tagTypes} 
                    disabled={tagTypeListLoading}
                    state={additionalFiltersState}
                />
            </Col>
        </Row>
    );
};

export default RevstackFiltersSubheader;
